/**
 * Offset rule parser for Smart Plans.
 *
 * Parses offset rules like "-7d", "+14d>mon", "-30d~wd 09:00", "same_day 07:00"
 * and computes concrete dates relative to an anchor date.
 *
 * Pure functions — no database or browser dependencies.
 */

import { AnchorDateType } from "../planTypes.js"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type OffsetUnit = "d" | "w" | "m" | "y"
export type SnapDirection = ">" | "<" | "~"
export type SnapToken = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun" | "wd" | "we"

export interface ParsedOffset {
  /** Number of units (always positive). */
  value: number
  /** Time unit. */
  unit: OffsetUnit
  /** Direction: -1 = before anchor, +1 = after anchor, 0 = same_day. */
  direction: -1 | 0 | 1
}

export interface ParsedSnap {
  direction: SnapDirection
  token: SnapToken
}

export interface ParsedTime {
  hour: number
  minute: number
}

export interface ParsedOffsetRule {
  offset: ParsedOffset
  snap?: ParsedSnap
  time?: ParsedTime
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SNAP_TOKENS = new Set<string>([
  "mon", "tue", "wed", "thu", "fri", "sat", "sun", "wd", "we",
])

const DAY_MAP: Record<string, number> = {
  sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6,
}

// ---------------------------------------------------------------------------
// Parser
// ---------------------------------------------------------------------------

const OFFSET_RE =
  /^(?:(same_day|anchor)|([+-])(\d+)([dwmy]))(?:([><~])([a-z]{2,3}))?(?:\s+(\d{2}):(\d{2}))?$/

/**
 * Parse an offset rule string into structured components.
 *
 * @example
 * parseOffsetRule("-7d")          // { offset: { value: 7, unit: "d", direction: -1 } }
 * parseOffsetRule("-2w>mon 09:00") // { offset: ..., snap: { direction: ">", token: "mon" }, time: { hour: 9, minute: 0 } }
 * parseOffsetRule("same_day 07:00") // { offset: { value: 0, unit: "d", direction: 0 }, time: { hour: 7, minute: 0 } }
 */
export function parseOffsetRule(rule: string): ParsedOffsetRule {
  const trimmed = rule.trim()
  const m = OFFSET_RE.exec(trimmed)
  if (!m) {
    throw new Error(`Invalid offset rule: "${rule}"`)
  }

  const [, sameDay, sign, num, unit, snapDir, snapToken, hours, minutes] = m

  let offset: ParsedOffset

  if (sameDay) {
    offset = { value: 0, unit: "d", direction: 0 }
  } else {
    offset = {
      value: parseInt(num!, 10),
      unit: unit as OffsetUnit,
      direction: sign === "-" ? -1 : 1,
    }
  }

  let snap: ParsedSnap | undefined
  if (snapDir && snapToken) {
    if (!SNAP_TOKENS.has(snapToken)) {
      throw new Error(`Invalid snap token: "${snapToken}" in rule "${rule}"`)
    }
    snap = {
      direction: snapDir as SnapDirection,
      token: snapToken as SnapToken,
    }
  }

  let time: ParsedTime | undefined
  if (hours !== undefined && minutes !== undefined) {
    time = { hour: parseInt(hours, 10), minute: parseInt(minutes, 10) }
  }

  return { offset, snap, time }
}

// ---------------------------------------------------------------------------
// Date computation (pure — uses only Date, no dayjs dependency)
// ---------------------------------------------------------------------------

/**
 * Compute a concrete date from an anchor date and an offset rule.
 *
 * @param anchorMs - Anchor date as UTC milliseconds (number).
 * @param offsetRule - Offset rule string, e.g. "-7d>sat 09:00".
 * @param anchorDateType - Determines scheduling direction for signed offsets.
 * @returns UTC milliseconds as a number.
 */
export function computeStepDate(
  anchorMs: number,
  offsetRule: string,
  anchorDateType: AnchorDateType,
): number {
  const parsed = parseOffsetRule(offsetRule)
  let date = new Date(anchorMs)

  // Apply offset
  if (parsed.offset.direction !== 0) {
    const effectiveDirection = resolveDirection(parsed.offset.direction, anchorDateType)
    date = applyOffset(date, parsed.offset.value, parsed.offset.unit, effectiveDirection)
  }

  // Apply snap
  if (parsed.snap) {
    date = applySnap(date, parsed.snap)
  }

  // Apply time
  if (parsed.time) {
    date.setUTCHours(parsed.time.hour, parsed.time.minute, 0, 0)
  }

  return date.getTime()
}

/**
 * Compute a reminder date from a step date and a reminder offset rule.
 *
 * Reminder offsets are always relative to the step date itself,
 * using forward scheduling (the offset direction is literal).
 *
 * @param stepMs - Step date as UTC milliseconds.
 * @param reminderOffset - Offset rule, e.g. "-2d 09:00".
 * @returns UTC milliseconds as a number.
 */
export function computeReminderDate(
  stepMs: number,
  reminderOffset: string,
): number {
  const parsed = parseOffsetRule(reminderOffset)
  let date = new Date(stepMs)

  // For reminders the sign is literal: -2d = 2 days before step
  if (parsed.offset.direction !== 0) {
    const dir = parsed.offset.direction === -1 ? -1 : 1
    date = applyOffset(date, parsed.offset.value, parsed.offset.unit, dir)
  }

  if (parsed.snap) {
    date = applySnap(date, parsed.snap)
  }

  if (parsed.time) {
    date.setUTCHours(parsed.time.hour, parsed.time.minute, 0, 0)
  }

  return date.getTime()
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Resolve the effective direction based on the offset sign and anchor type.
 *
 * For deadline/event: negative offsets go backward, positive forward.
 * For start/rolling: all offsets go forward (the sign in the rule is literal).
 */
function resolveDirection(
  direction: -1 | 1,
  _anchorDateType: AnchorDateType,
): -1 | 1 {
  // The offset sign in the rule is always literal:
  // "-7d" = 7 days before, "+14d" = 14 days after.
  return direction
}

/** Apply an numeric offset to a date. */
function applyOffset(date: Date, value: number, unit: OffsetUnit, direction: -1 | 1): Date {
  const result = new Date(date.getTime())
  const delta = value * direction

  switch (unit) {
    case "d":
      result.setUTCDate(result.getUTCDate() + delta)
      break
    case "w":
      result.setUTCDate(result.getUTCDate() + delta * 7)
      break
    case "m":
      result.setUTCMonth(result.getUTCMonth() + delta)
      break
    case "y":
      result.setUTCFullYear(result.getUTCFullYear() + delta)
      break
  }

  return result
}

/** Apply a snap modifier to a date. */
export function applySnap(date: Date, snap: ParsedSnap): Date {
  const result = new Date(date.getTime())
  const dow = result.getUTCDay() // 0=Sun, 6=Sat

  if (snap.token === "wd") {
    // Snap to nearest weekday (Mon-Fri)
    return snapToWeekday(result, snap.direction, dow)
  }

  if (snap.token === "we") {
    // Snap to nearest weekend (Sat-Sun)
    return snapToWeekend(result, snap.direction, dow)
  }

  // Specific day: mon, tue, ..., sun
  const targetDow = DAY_MAP[snap.token]!
  return snapToDay(result, snap.direction, dow, targetDow)
}

function snapToWeekday(date: Date, direction: SnapDirection, dow: number): Date {
  if (dow >= 1 && dow <= 5) return date // Already a weekday

  if (direction === ">") {
    // Forward to Monday
    const daysToAdd = dow === 0 ? 1 : 2
    date.setUTCDate(date.getUTCDate() + daysToAdd)
  } else if (direction === "<") {
    // Backward to Friday
    const daysToSub = dow === 0 ? 2 : 1
    date.setUTCDate(date.getUTCDate() - daysToSub)
  } else {
    // Nearest: Saturday→Friday, Sunday→Monday
    if (dow === 6) date.setUTCDate(date.getUTCDate() - 1) // Sat → Fri
    else date.setUTCDate(date.getUTCDate() + 1) // Sun → Mon
  }
  return date
}

function snapToWeekend(date: Date, direction: SnapDirection, dow: number): Date {
  if (dow === 0 || dow === 6) return date // Already weekend

  if (direction === ">") {
    // Forward to Saturday
    const daysToAdd = 6 - dow
    date.setUTCDate(date.getUTCDate() + daysToAdd)
  } else if (direction === "<") {
    // Backward to Sunday
    const daysToSub = dow
    date.setUTCDate(date.getUTCDate() - daysToSub)
  } else {
    // Nearest weekend day
    const toSat = 6 - dow
    const toSun = dow // distance backward to last Sunday
    if (toSat <= toSun) {
      date.setUTCDate(date.getUTCDate() + toSat)
    } else {
      date.setUTCDate(date.getUTCDate() - toSun)
    }
  }
  return date
}

function snapToDay(
  date: Date,
  direction: SnapDirection,
  currentDow: number,
  targetDow: number,
): Date {
  if (currentDow === targetDow) return date

  if (direction === ">") {
    const diff = (targetDow - currentDow + 7) % 7
    date.setUTCDate(date.getUTCDate() + diff)
  } else if (direction === "<") {
    const diff = (currentDow - targetDow + 7) % 7
    date.setUTCDate(date.getUTCDate() - diff)
  } else {
    // Nearest
    const forward = (targetDow - currentDow + 7) % 7
    const backward = (currentDow - targetDow + 7) % 7
    if (forward <= backward) {
      date.setUTCDate(date.getUTCDate() + forward)
    } else {
      date.setUTCDate(date.getUTCDate() - backward)
    }
  }
  return date
}
